import { useRef, useState } from "react";
import { Paperclip, Upload } from "@phosphor-icons/react";
import AttachmentThumb from "./attachments/AttachmentThumb";
import AttachmentLightbox from "./attachments/AttachmentLightbox";
import useAttachments from "./attachments/useAttachments";

function Dropzone({ onDrop, busy, progress, inputRef, onFileSelect, testId }) {
  const [dragOver, setDragOver] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); onDrop(Array.from(e.dataTransfer.files || [])); }}
      className={`border-2 border-dashed px-4 py-3 text-center text-xs transition-colors ${dragOver ? "border-[#FF3E00] bg-[#FF3E00]/5 text-[#FF3E00]" : "border-zinc-200 text-zinc-400"}`}
    >
      {busy ? (
        <div className="space-y-1">
          <div className="font-mono">UPLOADING… {progress}%</div>
          <div className="h-1 bg-zinc-200 overflow-hidden">
            <div className="h-full bg-[#FF3E00] transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      ) : (
        <span className="font-mono">DROP FILES HERE · 100 MB max · photos, video, PDF, docs</span>
      )}
      <input
        ref={inputRef}
        type="file"
        multiple
        onChange={onFileSelect}
        className="hidden"
        data-testid={testId}
      />
    </div>
  );
}

export default function AttachmentsPanel({ parentType, parentId, canAttach, currentUser, compact = false }) {
  const { items, busy, progress, upload, deleteOne } = useAttachments({ parentType, parentId });
  const [active, setActive] = useState(null);
  const inputRef = useRef(null);
  const canDeleteAtt = (att) => currentUser?.role === "admin" || att.uploaded_by_id === currentUser?.id;

  return (
    <div className="space-y-3" data-testid={`attachments-${parentType}-${parentId}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-400 uppercase flex items-center gap-1">
          <Paperclip size={11} /> ATTACHMENTS ({items.length})
        </span>
        {items.map((a) => (
          <AttachmentThumb key={a.id} att={a} onClick={setActive} onDelete={deleteOne} canDelete={canDeleteAtt(a)} />
        ))}
        {canAttach && !compact && (
          <button
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="w-20 h-20 border-2 border-dashed border-zinc-300 hover:border-zinc-950 hover:bg-zinc-50 flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-50"
            data-testid={`attachment-upload-btn-${parentType}-${parentId}`}
          >
            <Upload size={18} className="text-zinc-400" />
            <span className="font-mono text-[9px] text-zinc-500">ADD</span>
          </button>
        )}
      </div>

      {canAttach && !compact && (
        <Dropzone
          onDrop={upload}
          busy={busy}
          progress={progress}
          inputRef={inputRef}
          onFileSelect={(e) => upload(Array.from(e.target.files || []))}
          testId={`attachment-file-input-${parentType}-${parentId}`}
        />
      )}

      <AttachmentLightbox att={active} onClose={() => setActive(null)} />
    </div>
  );
}
